import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CircularProgress,
    Tooltip,
    Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ReportOutput from "src/scenes/ai-reports/components/ReportOutput";
import { getReport, refreshReport } from "src/scenes/ai-reports/ai-reports-service";

const TemplateAiReport = ({ savedReportId, title: titleOverride, liveOutput }) => {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!savedReportId) return;
        setLoading(true);
        getReport(savedReportId)
            .then(setReport)
            .catch(() => setError("Report not found or no longer available"))
            .finally(() => setLoading(false));
    }, [savedReportId]);

    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            await refreshReport(savedReportId, (event) => {
                if (event.type === "complete" && event.report) {
                    setReport(event.report);
                }
            });
        } finally {
            setRefreshing(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={2}>
                <Typography variant="body2" color="text.secondary">
                    {error}
                </Typography>
            </Box>
        );
    }

    // liveOutput comes from fetchAction (template batch) when isDynamic — fallback to cached
    const output = liveOutput ?? report?.output;
    const isDynamic = !!liveOutput;

    return (
        <Card variant="outlined" sx={{ height: "100%" }}>
            <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Typography variant="subtitle2" fontWeight={600}>
                        {titleOverride || report?.title}
                    </Typography>
                    {isDynamic && (
                        <Tooltip title="Live data — filtered to this dashboard's context">
                            <AutorenewIcon fontSize="small" color="primary" sx={{ opacity: 0.6 }} />
                        </Tooltip>
                    )}
                </Box>
                {output && <ReportOutput output={output} />}
            </CardContent>
            <CardActions sx={{ px: 2, pb: 1.5 }}>
                <Button
                    size="small"
                    startIcon={refreshing ? <CircularProgress size={12} /> : <RefreshIcon />}
                    disabled={refreshing || !report?.sqlQuery}
                    onClick={handleRefresh}
                >
                    Refresh
                </Button>
            </CardActions>
        </Card>
    );
};

export default TemplateAiReport;
